import { 
  getPoliticians as getPoliticiansThunk,
  getCommittees as getCommitteesThunk,
  getPoliticianTransactions as getPoliticianTransactionsThunk,
  getStockTransactions as getStockTransactionsThunk,
  getAllCongressionalTransactions as getAllCongressionalTransactionsThunk,
  getCongressionalSummary as getCongressionalSummaryThunk
} from '../api/thunks/congressional';
import { Politician, Committee } from '../api/models/models';

// Interface for getting access token - this should be implemented by the consuming component
interface TokenProvider {
  getAccessToken(): string | null;
}

let tokenProvider: TokenProvider | null = null;

// Set the token provider (called by components with access to Redux store)
export const setTokenProvider = (provider: TokenProvider) => {
  tokenProvider = provider;
};

// Helper function to get access token
const getAccessToken = (): string => {
  if (!tokenProvider) {
    throw new Error('Token provider not set. Call setTokenProvider first.');
  }
  
  const token = tokenProvider.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }
  
  return token;
};

// Congressional API service with automatic token handling
export const congressionalApi = {
  // Get politicians with optional filtering and pagination
  async getPoliticians(params: Parameters<typeof getPoliticiansThunk>[1] = {}): Promise<Politician[]> {
    return getPoliticiansThunk(getAccessToken(), params);
  },

  // Get committees
  async getCommittees(): Promise<Committee[]> {
    return getCommitteesThunk(getAccessToken());
  },

  // Get transactions for a specific politician
  async getPoliticianTransactions(
    politicianId: string, 
    params: Parameters<typeof getPoliticianTransactionsThunk>[2] = {}
  ): Promise<any> {
    return getPoliticianTransactionsThunk(getAccessToken(), politicianId, params);
  },

  // Get transactions for a specific stock/asset
  async getStockTransactions(
    symbol: string, 
    params: Parameters<typeof getStockTransactionsThunk>[2] = {}
  ): Promise<any> {
    return getStockTransactionsThunk(getAccessToken(), symbol, params);
  },

  // Get all congressional transactions
  async getAllTransactions(
    params: Parameters<typeof getAllCongressionalTransactionsThunk>[1] = {}
  ): Promise<any> {
    return getAllCongressionalTransactionsThunk(getAccessToken(), params);
  },

  // Get congressional summary/analytics
  async getCongressionalSummary(
    params: Parameters<typeof getCongressionalSummaryThunk>[1] = {}
  ): Promise<any> {
    return getCongressionalSummaryThunk(getAccessToken(), params);
  },
};

export default congressionalApi;
